import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Box, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../../components/ui/colors";
import { RELEASE_SCHEDULE } from "../config";
import { useNextRelease, type NextRelease } from "../hooks/useNextRelease";

const formatCountdown = ({ days, hours, minutes }: NextRelease): string => {
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
};

type ReleaseBannerProps = {
  placement?: "center" | "right";
};

export default function ReleaseBanner({ placement = "center" }: ReleaseBannerProps) {
  const navigate = useNavigate();
  const next = useNextRelease();
  const isRightPlacement = placement === "right";

  const tooltip = (
    <Box sx={{ p: 0.5, textAlign: "center" }}>
      <Typography variant="body2" fontWeight={700}>
        Weekly release
      </Typography>
      <Typography variant="caption" sx={{ display: "block" }}>
        {RELEASE_SCHEDULE.label}
      </Typography>
      <Typography variant="caption" sx={{ display: "block" }}>
        Next: {next.target.toFormat("cccc, LLL d")}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        flexGrow: isRightPlacement ? 0 : 1,
        flexShrink: 0,
        display: "flex",
        justifyContent: isRightPlacement ? "flex-end" : "center",
        minWidth: 0,
        px: isRightPlacement ? 0 : 1,
      }}
    >
      <Tooltip title={tooltip} arrow>
        <Box
          role="button"
          onClick={() => navigate("/releases")}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: isRightPlacement ? 0.75 : { xs: 0.75, md: 1 },
            maxWidth: "100%",
            px: isRightPlacement ? 1 : { xs: 1.25, md: 1.75 },
            py: isRightPlacement ? 0.45 : { xs: 0.4, md: 0.6 },
            borderRadius: 999,
            color: colors.orange[900],
            backgroundColor: colors.orange[50],
            border: `1px solid ${colors.orange[200]}`,
            transition: "background-color 160ms ease",
            "&:hover": {
              backgroundColor: colors.orange[100],
            },
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: isRightPlacement ? 16 : { xs: 17, md: 19 }, color: colors.orange.base }} />
          <Typography
            noWrap
            sx={{
              fontWeight: 600,
              fontSize: isRightPlacement ? "0.72rem" : { xs: "0.72rem", md: "0.83rem" },
            }}
          >
            {isRightPlacement ? "Release Monday" : "New release every Monday"}
          </Typography>
          <Typography
            noWrap
            sx={{
              display: { xs: "none", lg: "block" },
              fontSize: "0.72rem",
              fontWeight: 700,
              color: colors.orange.base,
            }}
          >
            {formatCountdown(next)}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}
